import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Link, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { DESCRIPTIVE_MARK, DIFF_LABEL, FindingDiffStatus, IEvidenceChange, IFinding, OWNER_LABEL, SEVERITY_LABEL } from "./raio-x.types";
import { evidenceChangeLine, evidenceLines } from "./evidenceFormat";

// why: MINOR usa a mesma cor neutra do chip de contagem em RaioXSections.tsx; MAJOR/BLOCKER são os únicos que precisam chamar atenção com cor.
const severityColor: Record<string, "error" | "warning" | "default"> = { BLOCKER: "error", MAJOR: "warning", MINOR: "default" };
// why: NOT_EVALUATED tem de usar a mesma cor neutra de PERSISTENT; nunca a cor de RESOLVED, senão a régua que nem rodou parece corrigida.
const diffColor: Record<FindingDiffStatus, "info" | "success" | "default"> = {
  NEW: "info",
  PERSISTENT: "default",
  RESOLVED: "success",
  NOT_EVALUATED: "default",
};

interface IProps {
  finding: IFinding;
  status: FindingDiffStatus | undefined;
  evidenceChange?: IEvidenceChange;
  workspaceLabels?: Record<string, string>;
}

export function FindingCard({ finding, status, evidenceChange, workspaceLabels = {} }: IProps) {
  const prescriptive = finding.nature === "PRESCRIPTIVE" && finding.severity;
  // why: fingerprint não inclui a lista da evidência; "persiste" sozinho não avisa que foi de 1 para 14 cargos.
  const showEvidenceChange = status === "PERSISTENT" && !!evidenceChange && evidenceChange.changes.length > 0;
  const opts = { workspaceLabels };
  const lines = evidenceLines(finding.evidence, opts);
  return (
    <Accordion disableGutters sx={{ border: "1px solid rgba(0,0,0,0.08)", boxShadow: "none", "&:before": { display: "none" }, mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600 }}>{finding.title}</Typography>
          {prescriptive ? (
            <Chip size="small" color={severityColor[finding.severity as string]} label={SEVERITY_LABEL[finding.severity as "BLOCKER"]} />
          ) : (
            // invariant: a marca do descritivo é sempre DESCRIPTIVE_MARK, nunca LENS_LABEL[lens] (um descritivo
            // de lente E diria "Quem ficou de fora", que é o nome da lente, não uma marca de conferência).
            <Chip size="small" variant="outlined" label={DESCRIPTIVE_MARK} />
          )}
          {status && <Chip size="small" variant="outlined" color={diffColor[status]} label={DIFF_LABEL[status]} />}
          {showEvidenceChange && <Chip size="small" variant="outlined" label="evidência mudou" />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {lines.length > 0 && (
            <Box sx={{ fontSize: 13, color: "text.secondary" }}>
              {lines.map((line, idx) => (
                <div key={idx}>{line}</div>
              ))}
            </Box>
          )}
          <Typography variant="body2">
            <strong>O que isso faz:</strong> {finding.consequence}
          </Typography>
          <Typography variant="body2">
            <strong>O que fazer:</strong> {finding.action} <em>({OWNER_LABEL[finding.owner]})</em>
          </Typography>
          {showEvidenceChange && evidenceChange && (
            <Box sx={{ fontSize: 13, color: "text.secondary" }}>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                Evidência mudou desde a última inspeção
              </Typography>
              {evidenceChange.changes.map((c) => (
                <div key={c.path}>{evidenceChangeLine(c, opts)}</div>
              ))}
            </Box>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            {finding.setupLink && (
              <Link href={finding.setupLink} underline="hover" variant="body2">
                Abrir configuração
              </Link>
            )}
            <Typography variant="caption" color="text.disabled">
              {finding.checkId}
            </Typography>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
